import type { EnrollmentQueueItem } from '../models/enrollment-queue.model';

/** NH / OE enrollment type codes from API. */
export function enrollmentTypeLabel(code: string | undefined): string {
  const c = String(code ?? '').trim().toUpperCase();
  if (c === 'NH') return 'New Hire';
  if (c === 'OE') return 'Open Enrollment';
  return code?.trim() ? code : '—';
}

/** Roll-up enrollment status display label. */
export function enrollmentStatusDisplayLabel(status: string | undefined): string {
  const s = String(status ?? '').trim();
  if (s === 'Pending') return 'Pending acknowledgment';
  return s || '—';
}

/** Text color class for enrollment type (NH / OE) — badge typography without pill chrome. */
export function enrollmentTypeTextClass(code: string | undefined): string {
  const c = String(code ?? '').trim().toUpperCase();
  switch (c) {
    case 'NH':
      return 'enrollment-type-text enrollment-type-text--nh';
    case 'OE':
      return 'enrollment-type-text enrollment-type-text--oe';
    default:
      return 'enrollment-type-text';
  }
}

/** Relative label for enrollment date, e.g. "2 days ago". */
export function enrollmentTimeAgoLabel(iso: string | undefined, now: Date = new Date()): string | null {
  if (!iso?.trim()) {
    return null;
  }
  const enrolled = new Date(iso.trim());
  if (Number.isNaN(enrolled.getTime())) {
    return null;
  }
  const diffMs = now.getTime() - enrolled.getTime();
  if (diffMs < 0) {
    return 'Just now';
  }
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 45) {
    return 'Just now';
  }
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return diffMin === 1 ? '1 minute ago' : `${diffMin} minutes ago`;
  }
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) {
    return diffHr === 1 ? '1 hour ago' : `${diffHr} hours ago`;
  }
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) {
    return diffDay === 1 ? '1 day ago' : `${diffDay} days ago`;
  }
  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) {
    return diffMonth === 1 ? '1 month ago' : `${diffMonth} months ago`;
  }
  const diffYear = Math.floor(diffMonth / 12);
  return diffYear === 1 ? '1 year ago' : `${diffYear} years ago`;
}

/** Subtitle under status badge: who approved/declined (hidden for pending). */
export function enrollmentApproverSubtitle(
  item: Pick<EnrollmentQueueItem, 'enrollmentStatus' | 'approvedBy' | 'approvedByUsername'>
): string | null {
  const user = String(item.approvedByUsername ?? item.approvedBy ?? '').trim();
  if (!user) {
    return null;
  }
  const status = String(item.enrollmentStatus ?? '').trim().toLowerCase();
  if (status === 'pending') {
    return null;
  }
  if (status === 'declined') {
    return `Declined by ${user}`;
  }
  if (status === 'approved' || status === 'auto') {
    return `Approved by ${user}`;
  }
  return `by ${user}`;
}

/** Badge class for enrollment queue status chips (aligned with member portal chips). */
export function enrollmentStatusBadgeClass(status: string | undefined): string {
  const s = String(status ?? '').trim().toLowerCase();
  switch (s) {
    case 'pending':
      return 'status-badge status-badge--pending';
    case 'approved':
      return 'status-badge status-badge--active';
    case 'auto':
      return 'status-badge status-badge--active';
    case 'declined':
      return 'status-badge status-badge--ended';
    default:
      return 'status-badge';
  }
}
